import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { nextCheckId } from '../expectations';

import { TagListInput } from './TagListInput';

import type { ScenarioExpectations, ScenarioRules } from '@/lib/api/scenarios.api';

interface ExpectationsEditorProps {
  value: ScenarioExpectations;
  onChange: (value: ScenarioExpectations) => void;
  disabled?: boolean;
}

const RULE_LISTS: Array<{
  key: keyof Pick<ScenarioRules, 'toolsMustCall' | 'toolsMustNotCall' | 'mustSay' | 'mustNotSay'>;
  label: string;
  hint: string;
  placeholder: string;
}> = [
  {
    key: 'toolsMustCall',
    label: 'Tools that must be called',
    hint: 'Tool names, one per entry.',
    placeholder: 'search_cars',
  },
  {
    key: 'toolsMustNotCall',
    label: 'Tools that must not be called',
    hint: 'The test fails if any of these run.',
    placeholder: 'transfer_call',
  },
  {
    key: 'mustSay',
    label: 'Agent must say',
    hint: 'Case-insensitive phrases that must appear in an agent turn.',
    placeholder: 'showroom',
  },
  {
    key: 'mustNotSay',
    label: 'Agent must not say',
    hint: 'Phrases that must never appear in an agent turn.',
    placeholder: 'monthly price',
  },
];

export const ExpectationsEditor = ({ value, onChange, disabled }: ExpectationsEditorProps) => {
  const { checks, rules } = value;

  const setChecks = (next: ScenarioExpectations['checks']) => onChange({ ...value, checks: next });
  const setRules = (patch: Partial<ScenarioRules>) =>
    onChange({ ...value, rules: { ...rules, ...patch } });

  const addCheck = () => setChecks([...checks, { id: nextCheckId(checks), text: '' }]);
  const updateCheck = (index: number, text: string) =>
    setChecks(checks.map((c, i) => (i === index ? { ...c, text } : c)));
  const removeCheck = (index: number) => setChecks(checks.filter((_, i) => i !== index));
  const moveCheck = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= checks.length) return;
    const next = [...checks];
    [next[index], next[target]] = [next[target], next[index]];
    setChecks(next);
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium">Checks</h3>
            <p className="text-muted-foreground text-xs">
              Plain-English statements an LLM judge verifies from the transcript and tool log.
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addCheck} disabled={disabled}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add check
          </Button>
        </div>

        {checks.length === 0 ? (
          <div className="text-muted-foreground rounded-lg border border-dashed py-6 text-center text-sm">
            No checks yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {checks.map((check, index) => (
              <li key={check.id} className="flex items-center gap-2">
                <span className="text-muted-foreground w-8 shrink-0 font-mono text-xs">
                  {check.id}
                </span>
                <Input
                  value={check.text}
                  disabled={disabled}
                  placeholder="e.g. the agent offers at least one showroom slot"
                  aria-label={`Check ${check.id}`}
                  onChange={(e) => updateCheck(index, e.target.value)}
                />
                <div className="flex shrink-0 gap-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label="Move up"
                    disabled={disabled || index === 0}
                    onClick={() => moveCheck(index, -1)}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label="Move down"
                    disabled={disabled || index === checks.length - 1}
                    onClick={() => moveCheck(index, 1)}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label="Remove check"
                    disabled={disabled}
                    onClick={() => removeCheck(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">Rules</h3>
          <p className="text-muted-foreground text-xs">
            Deterministic checks evaluated without a model. Leave blank to skip.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {RULE_LISTS.map((rule) => (
            <div key={rule.key} className="space-y-1">
              <label htmlFor={`rule-${rule.key}`} className="text-xs font-medium">
                {rule.label}
              </label>
              <TagListInput
                id={`rule-${rule.key}`}
                values={rules[rule.key]}
                disabled={disabled}
                placeholder={rule.placeholder}
                onChange={(next) => setRules({ [rule.key]: next })}
              />
              <p className="text-muted-foreground text-xs">{rule.hint}</p>
            </div>
          ))}

          <div className="space-y-1">
            <label htmlFor="rule-outcome" className="text-xs font-medium">
              Expected outcome
            </label>
            <Input
              id="rule-outcome"
              value={rules.outcome ?? ''}
              disabled={disabled}
              placeholder="e.g. appointment_booked"
              onChange={(e) => setRules({ outcome: e.target.value === '' ? null : e.target.value })}
            />
            <p className="text-muted-foreground text-xs">
              The call outcome value the conversation must end with.
            </p>
          </div>

          <div className="space-y-1">
            <label htmlFor="rule-max-turns" className="text-xs font-medium">
              Max turns
            </label>
            <Input
              id="rule-max-turns"
              type="number"
              min={1}
              max={60}
              value={rules.maxTurns ?? ''}
              disabled={disabled}
              placeholder="no limit"
              onChange={(e) =>
                setRules({ maxTurns: e.target.value === '' ? null : Number(e.target.value) })
              }
            />
            <p className="text-muted-foreground text-xs">
              Fail if the agent needs more turns than this to get there.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
